import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { getAdminSocket } from '../../services/socketClient'

/**
 * Pushes a casino table's feed into the React Query cache as it arrives — the
 * admin copy of frontend/src/hooks/casino/useCasinoFeedSocket.js.
 *
 * The warmer (server/casinobet/cron/casinoAllDataCron.js) publishes a table the
 * moment its payload changes and the server relays it to the `casino_<type>`
 * room. With only the 1s REST poll, the admin timer sat on one number for 2-3s
 * and then jumped (the poll's own latency on top of the feed's steps); the
 * player site already takes the push and counts down smoothly.
 *
 * It writes into the SAME cache key the poll fills, in the REST envelope
 * ({ success, data }), so the hook's `select` reads a push exactly like a
 * fetch. The poll stays underneath as the fallback.
 * Plan: docs/plans/2026-10-15-5five-cricket-audit.md
 */
export default function useCasinoFeedSocket(gameType, queryKey, enabled = true) {
  const queryClient = useQueryClient()
  const token = useSelector((s) => s.auth.token)
  // The key is rebuilt on every render, so depend on its contents.
  const keyId = JSON.stringify(queryKey)

  useEffect(() => {
    if (!enabled || !gameType || !token) return undefined
    const socket = getAdminSocket(token)
    if (!socket) return undefined

    const onData = (payload) => {
      // A socket that just switched tables can still deliver the previous one.
      if (!payload || payload.type !== gameType) return
      queryClient.setQueryData(JSON.parse(keyId), { success: true, data: payload.data })
    }
    const subscribe = () => socket.emit('subscribeCasino', { type: gameType })

    socket.on('casinoData', onData)
    // The room is server-side state, lost on a reconnect — re-join every connect.
    socket.on('connect', subscribe)
    if (socket.connected) subscribe()

    return () => {
      socket.off('casinoData', onData)
      socket.off('connect', subscribe)
      socket.emit('unsubscribeCasino')
    }
  }, [enabled, gameType, keyId, queryClient, token])
}
